import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordResetRequired, setPasswordResetRequired] = useState(
    () => localStorage.getItem('password_reset_required') === 'true'
  );

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('[Auth] Checking authentication with token...');
        const response = await userAPI.getMe();
        setUser(response.data.user);
        setBusinesses(response.data.businesses);
        setIsAuthenticated(true);
        
        console.log('[Auth] Auth check successful', {
          userId: response.data.user?.id,
          role: response.data.user?.role,
          businessCount: response.data.businesses?.length
        });
      } else {
        console.log('[Auth] No token found - user not authenticated');
      }
    } catch (error) {
      console.error('[Auth] Auth check failed:', error);
      // Only clear auth tokens, not all localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('active_business');
      localStorage.removeItem('active_business_id');
      localStorage.removeItem('selectedBusinessId');
      localStorage.removeItem('user_businesses');
      setUser(null);
      setBusinesses([]);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      console.log('[Auth] Auth check complete - loading set to false');
    }
  };

  const login = async (phoneOrEmail, password) => {
    try {
      const response = await authAPI.login({
        phone_or_email: phoneOrEmail,
        password,
      });

      const { user, businesses, access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      // Clear stale business selection from any previous session
      localStorage.removeItem('active_business');
      localStorage.removeItem('active_business_id');
      localStorage.removeItem('selectedBusinessId');
      localStorage.removeItem('user_type');
      localStorage.removeItem('staff_info');
      // Store businesses with role info for later use
      localStorage.setItem('user_businesses', JSON.stringify(businesses));

      setUser(user);
      setBusinesses(businesses);
      setIsAuthenticated(true);

      return { success: true, user, businesses };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  // Staff login uses dedicated endpoint with different response structure
  const staffLogin = async (phoneOrEmail, password) => {
    try {
      const response = await api.post('/settings/users/field-staff/login', {
        phone_or_email: phoneOrEmail,
        password,
      });

      const data = response.data;
      const { staff, business, access_token, refresh_token, user_type } = data;

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user_type', user_type || 'staff');
      localStorage.setItem('staff_info', JSON.stringify(staff));

      // Track password reset requirement
      const needsReset = staff?.password_reset_required === true;
      localStorage.setItem('password_reset_required', needsReset ? 'true' : 'false');
      setPasswordResetRequired(needsReset);
      
      // Store business info for BusinessContext
      localStorage.setItem('active_business_id', business.id);
      localStorage.setItem('active_business', JSON.stringify(business));

      // Create a user object from staff data for consistency
      const staffUser = {
        id: staff.id,
        name: staff.full_name,
        email: staff.email,
        mobile: staff.mobile,
        role: 'STAFF',
        user_type: 'staff',
        permissions: staff.permissions,
        role_template: staff.role_template,
        business_id: staff.business_id,
      };

      // Create business object from response
      const staffBusiness = {
        id: business.id,
        name: business.name,
        user_role: 'STAFF',
        is_owner: false,
      };
      
      // Store businesses for user_businesses
      localStorage.setItem('user_businesses', JSON.stringify([staffBusiness]));

      setUser(staffUser);
      setBusinesses([staffBusiness]);
      setIsAuthenticated(true);

      return { 
        success: true, 
        user: staffUser, 
        businesses: [staffBusiness],
        staff,
        business,
        isStaff: true
      };
    } catch (error) {
      console.error('Staff login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Staff login failed. Please try again.',
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);

      const { user, access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setUser(user);
      setIsAuthenticated(true);

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear only auth-related keys (not all localStorage to preserve app preferences)
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('active_business');
      localStorage.removeItem('active_business_id');
      localStorage.removeItem('selectedBusinessId');
      localStorage.removeItem('user_businesses');
      localStorage.removeItem('user_type');
      localStorage.removeItem('staff_info');
      localStorage.removeItem('user');
      localStorage.removeItem('password_reset_required');

      setUser(null);
      setBusinesses([]);
      setIsAuthenticated(false);
      setPasswordResetRequired(false);
      
      console.log('[Auth] Logout complete - auth keys cleared');
    }
  };

  // Kept for backward compatibility -- pages that still use fetch() directly
  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  };

  const clearPasswordResetRequired = () => {
    localStorage.setItem('password_reset_required', 'false');
    setPasswordResetRequired(false);
  };

  const value = {
    user,
    businesses,
    isAuthenticated,
    loading,
    login,
    staffLogin,
    register,
    logout,
    refreshUserData: checkAuth,
    getHeaders,
    passwordResetRequired,
    clearPasswordResetRequired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};